import React, { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { createSchemModelRequest } from "store/schemaModels/actions";
import { buildToolError } from "./errorEnvelope";
import { buildSchemaPayload, type AssistantFieldInput } from "./payload";
import type { ToolPartProps } from "./types";

interface CreateSchemaInput {
    name: string;
    description: string;
    fields: AssistantFieldInput[];
}

interface CreateSchemaOutput {
    success: boolean;
    schemaId?: string;
    schemaName?: string;
    cancelled?: boolean;
    error?: string;
}

export default function CreateSchemaTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<Partial<CreateSchemaInput>, CreateSchemaOutput>) {
    const [isPending, setIsPending] = useState(false);
    const { dispatch, businessAreaId, addToolOutput, createSchemaModelResult, createdSchemaModel } = helpers;

    // The create-schema redux slice persists the result of the LAST create
    // across the whole session. To avoid reacting to a stale `{ success: true }`
    // left over from a previous create, we snapshot the result reference at
    // dispatch time and only act once a NEW result object arrives.
    const resultAtDispatchRef = useRef<unknown>(null);

    useEffect(() => {
        if (!isPending || state === "output-available" || state === "output-error") return;
        if (!createSchemaModelResult) return;
        // Still showing the pre-dispatch result — the new create hasn't resolved yet.
        if (createSchemaModelResult === resultAtDispatchRef.current) return;
        if (createSchemaModelResult.success) {
            // The create POST is authoritative — a 200 means the schema was
            // persisted. We report success directly from the response rather
            // than re-fetching (a read-after-write re-fetch races DynamoDB
            // eventual consistency and produced false "schema was not found
            // after creation" warnings).
            addToolOutput({
                tool: "createSchema",
                toolCallId,
                output: {
                    success: true,
                    schemaId: createdSchemaModel?.schemaId,
                    schemaName: createdSchemaModel?.name || input?.name,
                },
            });
            setIsPending(false);
        } else if (createSchemaModelResult.success === false) {
            addToolOutput({
                tool: "createSchema",
                toolCallId,
                output: buildToolError(createSchemaModelResult, "Create failed."),
            });
            setIsPending(false);
        }
    }, [createSchemaModelResult, createdSchemaModel, isPending, state, toolCallId, addToolOutput, input]);

    const fields = Array.isArray(input?.fields) ? input!.fields! : [];

    const handleCreate = () => {
        if (!businessAreaId) {
            addToolOutput({
                tool: "createSchema",
                toolCallId,
                output: { success: false, error: "Business area context not loaded — open a business area first." },
            });
            return;
        }
        const schemaModel = buildSchemaPayload(
            {
                name: input?.name || "",
                description: input?.description || "",
                fields,
            },
            businessAreaId,
        );
        // Snapshot the current (stale) result so the effect ignores it and
        // only fires when the saga produces a fresh result object.
        resultAtDispatchRef.current = createSchemaModelResult;
        setIsPending(true);
        dispatch(createSchemModelRequest(businessAreaId, schemaModel));
    };

    const handleCancel = () => {
        addToolOutput({ tool: "createSchema", toolCallId, output: { success: false, cancelled: true } });
    };

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <PlusOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Proposed schema</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{input?.name || "(unnamed schema)"}</div>
            {input?.description && <div className="mt-1 text-xs text-zinc-600">{input.description}</div>}
            {fields.length > 0 && (
                <div className="mt-3 overflow-hidden rounded-md border border-zinc-200">
                    <table className="w-full border-collapse text-left text-xs">
                        <thead className="bg-zinc-100 text-zinc-700">
                            <tr>
                                <th className="px-2 py-1 font-medium">Field</th>
                                <th className="px-2 py-1 font-medium">Type</th>
                                <th className="px-2 py-1 font-medium">Flags</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((f, i) => (
                                <tr key={`${f.name}-${i}`} className="border-t border-zinc-200">
                                    <td className="px-2 py-1">
                                        <div className="font-medium text-zinc-900">{f.name}</div>
                                        {f.description && <div className="text-[10px] text-zinc-500">{f.description}</div>}
                                    </td>
                                    <td className="px-2 py-1 align-top text-zinc-700">{f.dataType}</td>
                                    <td className="px-2 py-1 align-top text-[10px] text-zinc-500">
                                        {[f.isPrimaryKey && "PK", f.isRequired && "Required", f.isPII && "PII"].filter(Boolean).join(" · ")}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {state === "output-available" && output?.success && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Schema created{output.schemaId ? ` (id: ${output.schemaId})` : ""}.
                </div>
            )}
            {state === "output-available" && !output?.success && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-available" && !output?.success && !output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {output?.error || "Create failed."}
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {(state === "input-streaming" || state === "input-available") && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isPending || state === "input-streaming"}
                        className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={isPending || state === "input-streaming" || fields.length === 0}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isPending ? (
                            <>
                                <LoadingOutlined spin /> Creating...
                            </>
                        ) : (
                            "Create"
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}
